import { FapService, FapsInner } from '@api/backend';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { ErrorResponse } from '@core/models';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { debounceTime, distinctUntilChanged, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import { sessionSelectors } from '@store/session';

type FapsState = {
  list: Array<FapsInner>
  isLoading: boolean;
  nextPage: number
  error: ErrorResponse | null
  subscribeModalWatchCount: number
  player: {
    muted: boolean
  }
};

const initialFapsState: FapsState = {
  list: [],
  isLoading: false,
  error: null,
  nextPage: 1,
  subscribeModalWatchCount: 0,
  player: {
    muted: true,
  },
};

export const FapsStore = signalStore(
  { providedIn: 'root' },
  withState(initialFapsState),
  withMethods((
    state,
    api = inject(FapService),
  ) => ({
    loadByQuery: rxMethod<{ t?: string, c?: string, r?: string, q?: string, a?: string, np?:boolean, ads?: boolean, p?: number, ps?: number, defaultList?: boolean }>(
      pipe(
        debounceTime(10),
        distinctUntilChanged(),
        tap(({ defaultList }) => patchState(state, { isLoading: true, list: defaultList ? [] : state.list() })),
        switchMap(({ t, c, r, q, a, np = true, ads = true, p, ps = 3, defaultList }) => {
          return api.getFapListFapGet(t, c, r, q, a, np, ads, p, ps).pipe(
            tapResponse({
              next: (response) => {
                patchState(state, {
                  list: defaultList ? [...response.data.faps] : [...state.list(), ...response.data.faps],
                  nextPage: response.data.next_page,
                });
              },
              error: () => {},
              finalize: () => patchState(state, { isLoading: false }),
            }),
          );
        }),
      ),
    ),
  })),
  withMethods((
    state,
    api = inject(FapService),
    globalStore = inject(Store),
  ) => ({
    loadById: rxMethod<{ fapId?: string }>(
      pipe(
        debounceTime(10),
        distinctUntilChanged(),
        tap(() => patchState(state, { isLoading: true, list: [] })),
        switchMap(({ fapId }) => {
          return api.getFapFapFapIdGet(fapId!).pipe(
            tapResponse({
              next: (response) => {
                patchState(state, { list: [response.data as FapsInner] });
                state.loadByQuery({ r: fapId });
              },
              error: () => {},
              finalize: () => patchState(state, { isLoading: false }),
            }),
          );
        }),
      ),
    ),
    like: rxMethod<{ fapId: string }>(
      pipe(
        debounceTime(10),
        filter(() => globalStore.selectSignal(sessionSelectors.selectIsSetUser)()),
        tap(({ fapId }) => {
          patchState(state, {
            list: state.list().map(item =>
              (item.fap_id === fapId) ? {
                ...item,
                is_liked: !item?.is_liked,
                likes: (item?.likes || 0) + 1,
              } : item,
            ) });
        }),
        switchMap(({ fapId }) => api.postFapLikeFapFapIdLikePost(fapId)),
      ),
    ),
    unlike: rxMethod<{ fapId: string }>(
      pipe(
        debounceTime(10),
        filter(() => globalStore.selectSignal(sessionSelectors.selectIsSetUser)()),
        tap(({ fapId }) => {
          patchState(state, {
            list: state.list().map(item =>
              (item.fap_id === fapId) ? {
                ...item,
                is_liked: !item?.is_liked,
                likes: (item?.likes || 0) - 1,
              } : item,
            ) });
        }),
        switchMap(({ fapId }) => api.postFapUnlikeFapFapIdUnlikePost(fapId)),
      ),
    ),
    share: rxMethod<{ fapId: string }>(
      pipe(
        debounceTime(10),
        filter(() => globalStore.selectSignal(sessionSelectors.selectIsSetUser)()),
        tap(({ fapId }) =>  {
          patchState(state, {
            list: state.list().map(item =>
              (item.fap_id === fapId) ? {
                ...item,
                shares: (item?.shares || 0) + 1,
              } : item,
            ) });
        }),
        switchMap(({ fapId }) => api.postFapShareFapFapIdSharePost(fapId)),

      ),
    ),
    trackView: rxMethod<{ id: string, duration: number }>(
      pipe(
        debounceTime(10),
        filter(() => globalStore.selectSignal(sessionSelectors.selectIsSetUser)()),
        switchMap(({ id, duration }) => api.postFapViewFapFapIdViewPost(id, { duration, swipes: 0 })),
      ),
    ),
    changeVolume(muted: boolean) {
      patchState(state, { player: { muted } });
    },
    resetSubscribeModalWatchCount() {
      patchState(state, { subscribeModalWatchCount: 0 });
    },
    incrementSubscribeModalWatchCount() {
      patchState(state, {
        subscribeModalWatchCount: state.subscribeModalWatchCount() + 1,
      });
    },
  })),
);
